import {
  ICoverageCellResponse,
  ICoverageCellRowBreakdown,
  ICoverageMatrixCell,
  ICoverageMatrixResponse,
  ICoverageModel,
  ICoverageRow,
} from 'types/api/coverageApi.interface'
import { IShiftCoverageSummaryResponse } from 'types/api/shiftCoverageApi.interface'

export const USE_COVERAGE_MOCKS = true

interface INamedDef {
  id: number
  name: string
}

const DEFAULT_SITES: INamedDef[] = [
  { id: 1, name: 'Site North' },
  { id: 2, name: 'Site South' },
]

const DEFAULT_DEPARTMENTS: INamedDef[] = [
  { id: 1, name: 'Mining' },
  { id: 2, name: 'Plant' },
  { id: 3, name: 'Maintenance' },
  { id: 4, name: 'Camp Services' },
  { id: 5, name: 'Security' },
]

let siteDefs: INamedDef[] = [...DEFAULT_SITES]
let deptDefs: INamedDef[] = [...DEFAULT_DEPARTMENTS]

export const coverageSites = (): INamedDef[] => siteDefs
export const coverageDepartments = (): INamedDef[] => deptDefs

interface ICoverageShiftDef {
  shiftId: number
  name: string
  windowFrom: string
  windowTo: string
}

const DEFAULT_SHIFTS: ICoverageShiftDef[] = [
  { shiftId: 1, name: 'Morning', windowFrom: '06:00', windowTo: '14:00' },
  { shiftId: 2, name: 'Afternoon', windowFrom: '14:00', windowTo: '22:00' },
  { shiftId: 3, name: 'Night', windowFrom: '22:00', windowTo: '06:00' },
  { shiftId: 4, name: 'Full Day', windowFrom: '06:00', windowTo: '18:00' },
  { shiftId: 9, name: 'test', windowFrom: '06:00', windowTo: '14:00' },
]

let shiftDefs: ICoverageShiftDef[] = [...DEFAULT_SHIFTS]
let registrySynced = false

export const coverageShifts = (): ICoverageShiftDef[] => shiftDefs

export const coverageRegistrySynced = (): boolean => registrySynced

const minToHHMM = (totalMin: number) => {
  const m = ((totalMin % 1440) + 1440) % 1440
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}

export const syncCoverageShiftsFromRegistry = (registry: any[]): void => {
  if (registrySynced) return
  const working = (registry || []).filter(
    (s: any) => s && !s.absence && s.shiftStart != null && s.minutes != null && s.minutes > 0,
  )
  if (!working.length) return
  const defs: ICoverageShiftDef[] = [...working]
    .sort((a: any, b: any) => a.shiftStart - b.shiftStart)
    .map((s: any) => ({
      shiftId: s.id,
      name: s.name || `Shift ${s.id}`,
      windowFrom: minToHHMM(s.shiftStart),
      windowTo: minToHHMM(s.shiftStart + s.minutes),
    }))
  const slotMap: { [slot: number]: number } = {}
  ;[1, 2, 3].forEach((slot, i) => {
    if (defs[i]) slotMap[slot] = defs[i].shiftId
  })
  models.forEach((m) => {
    if (slotMap[m.shiftId] != null) m.shiftId = slotMap[m.shiftId]
  })
  const oldPools = { ...POOLS }
  Object.keys(POOLS).forEach((k) => delete POOLS[k])
  Object.entries(oldPools).forEach(([key, value]) => {
    const [site, dept, shift] = key.split('-').map(Number)
    const mapped = slotMap[shift] ?? shift
    POOLS[`${site}-${dept}-${mapped}`] = value
  })
  shiftDefs = defs
  registrySynced = true
}

let sitesSynced = false
let deptsSynced = false
let rolesSynced = false
let capsSynced = false

const remapPoolDim = (dim: number, remap: { [old: number]: number }) => {
  const old = { ...POOLS }
  Object.keys(POOLS).forEach((k) => delete POOLS[k])
  Object.entries(old).forEach(([key, value]) => {
    const parts = key.split('-').map(Number)
    parts[dim] = remap[parts[dim]] ?? parts[dim]
    POOLS[`${parts[0]}-${parts[1]}-${parts[2]}`] = value
  })
}

const remapMembersDim = (dim: number, remap: { [old: number]: number }) => {
  const old = { ...COVERAGE_DEPT_MEMBERS }
  Object.keys(COVERAGE_DEPT_MEMBERS).forEach((k) => delete COVERAGE_DEPT_MEMBERS[k])
  Object.entries(old).forEach(([key, value]) => {
    const parts = key.split('-').map(Number)
    parts[dim] = remap[parts[dim]] ?? parts[dim]
    COVERAGE_DEPT_MEMBERS[`${parts[0]}-${parts[1]}`] = value
  })
}

const namedList = (list: any[]): INamedDef[] =>
  (list || [])
    .filter((x: any) => x && x.id != null && x.name)
    .map((x: any) => ({ id: Number(x.id), name: String(x.name) }))

const slotRemap = (defaults: INamedDef[], real: INamedDef[]): { [old: number]: number } => {
  const remap: { [old: number]: number } = {}
  const taken = new Set<number>()
  defaults.forEach((d) => {
    const match = real.find((r) => !taken.has(r.id) && r.name.toLowerCase() === d.name.toLowerCase())
    if (match) {
      remap[d.id] = match.id
      taken.add(match.id)
    }
  })
  defaults.forEach((d) => {
    if (remap[d.id] != null) return
    const match = real.find((r) => !taken.has(r.id))
    if (match) {
      remap[d.id] = match.id
      taken.add(match.id)
    }
  })
  return remap
}

export const syncCoverageSitesFromRegistry = (treeRoot: any): void => {
  if (sitesSynced || !treeRoot) return
  const roots: any[] =
    treeRoot.id === 0 && treeRoot.name === 'Sites' && Array.isArray(treeRoot.children)
      ? treeRoot.children
      : [treeRoot]
  const realSites = namedList(roots)
  if (!realSites.length) return
  const locs: ICoverageLocationDef[] = []
  const walk = (node: any, rootId: number) => {
    ;(node.children || []).forEach((c: any) => {
      if (c && c.id != null && c.name) {
        locs.push({ id: Number(c.id), siteNodeId: rootId, name: String(c.name) })
        walk(c, rootId)
      }
    })
  }
  roots.forEach((r: any) => walk(r, Number(r.id)))
  const siteRemap = slotRemap(DEFAULT_SITES, realSites)
  models.forEach((m) => {
    if (siteRemap[m.siteNodeId] != null) m.siteNodeId = siteRemap[m.siteNodeId]
  })
  remapPoolDim(0, siteRemap)
  remapMembersDim(0, siteRemap)
  const locRemap: { [old: number]: number } = {}
  DEFAULT_LOCATIONS.forEach((dl) => {
    const mappedSite = siteRemap[dl.siteNodeId] ?? dl.siteNodeId
    const match = locs.find((l) => l.siteNodeId === mappedSite && l.name.toLowerCase() === dl.name.toLowerCase())
    if (match) locRemap[dl.id] = match.id
    else {
      const ghost = -dl.id
      locs.push({ id: ghost, siteNodeId: mappedSite, name: dl.name })
      locRemap[dl.id] = ghost
    }
  })
  models.forEach((m) =>
    m.rows.forEach((r) => {
      if (r.locationNodeId != null && locRemap[r.locationNodeId] != null) r.locationNodeId = locRemap[r.locationNodeId]
    }),
  )
  siteDefs = realSites
  locDefs = locs
  sitesSynced = true
}

export const syncCoverageDepartmentsFromRegistry = (list: any[]): void => {
  if (deptsSynced) return
  const real = namedList(list)
  if (!real.length) return
  const remap = slotRemap(DEFAULT_DEPARTMENTS, real)
  models.forEach((m) => {
    if (remap[m.departmentId] != null) m.departmentId = remap[m.departmentId]
  })
  remapPoolDim(1, remap)
  remapMembersDim(1, remap)
  deptDefs = real
  deptsSynced = true
}

const syncNamedWithGhosts = (
  defaults: INamedDef[],
  list: any[],
  applyRemap: (remap: { [old: number]: number }) => void,
): INamedDef[] | null => {
  const real = namedList(list)
  if (!real.length) return null
  const defs = [...real]
  const remap: { [old: number]: number } = {}
  defaults.forEach((d) => {
    const match = real.find((r) => r.name.toLowerCase() === d.name.toLowerCase())
    if (match) remap[d.id] = match.id
    else {
      const ghost = -d.id
      defs.push({ id: ghost, name: d.name })
      remap[d.id] = ghost
    }
  })
  applyRemap(remap)
  return defs
}

export const syncCoverageRolesFromRegistry = (list: any[]): void => {
  if (rolesSynced) return
  const defs = syncNamedWithGhosts(DEFAULT_ROLES, list, (remap) =>
    models.forEach((m) =>
      m.rows.forEach((r) => {
        if (r.requirementType === 'role' && r.roleId != null && remap[r.roleId] != null) r.roleId = remap[r.roleId]
      }),
    ),
  )
  if (!defs) return
  roleDefs = defs
  rolesSynced = true
}

export const syncCoverageCapabilitiesFromRegistry = (list: any[]): void => {
  if (capsSynced) return
  const defs = syncNamedWithGhosts(DEFAULT_CAPABILITIES, list, (remap) =>
    models.forEach((m) =>
      m.rows.forEach((r) => {
        if (r.requirementType === 'capability' && r.capabilityId != null && remap[r.capabilityId] != null) {
          r.capabilityId = remap[r.capabilityId]
        }
      }),
    ),
  )
  if (!defs) return
  capDefs = defs
  capsSynced = true
}

export const syncCoverageRegistries = (rootState: any): void => {
  syncCoverageShiftsFromRegistry(rootState?.shifts?.shifts || [])
  syncCoverageSitesFromRegistry(rootState?.locationRegistry?.tree || null)
  syncCoverageDepartmentsFromRegistry(rootState?.departments?.departments || [])
  syncCoverageRolesFromRegistry(rootState?.clientRoles?.roles || [])
  syncCoverageCapabilitiesFromRegistry(rootState?.capabilities?.data || [])
}

const DEFAULT_ROLES: INamedDef[] = [
  { id: 1, name: 'Operator' },
  { id: 2, name: 'Truck driver' },
  { id: 3, name: 'Plant operator' },
  { id: 4, name: 'Mechanic' },
  { id: 5, name: 'Camp manager' },
  { id: 6, name: 'Camp supervisor' },
  { id: 7, name: 'Cleaner' },
  { id: 8, name: 'Cook' },
  { id: 9, name: 'Gate guard' },
]

const DEFAULT_CAPABILITIES: INamedDef[] = [
  { id: 101, name: 'MECH-2' },
  { id: 102, name: 'HV License' },
  { id: 103, name: 'First Aid' },
]

interface ICoverageLocationDef {
  id: number
  siteNodeId: number
  name: string
}

const DEFAULT_LOCATIONS: ICoverageLocationDef[] = [
  { id: 11, siteNodeId: 1, name: 'Main Gate' },
  { id: 12, siteNodeId: 1, name: 'East Gate' },
  { id: 13, siteNodeId: 1, name: 'Control Room' },
  { id: 14, siteNodeId: 1, name: 'Camp Kitchen' },
  { id: 15, siteNodeId: 1, name: 'Workshop' },
  { id: 21, siteNodeId: 2, name: 'South Gate' },
  { id: 22, siteNodeId: 2, name: 'ROM Pad' },
]

let roleDefs: INamedDef[] = [...DEFAULT_ROLES]
let capDefs: INamedDef[] = [...DEFAULT_CAPABILITIES]
let locDefs: ICoverageLocationDef[] = [...DEFAULT_LOCATIONS]

export const coverageRoles = (): INamedDef[] => roleDefs
export const coverageCapabilities = (): INamedDef[] => capDefs
export const coverageLocations = (): ICoverageLocationDef[] => locDefs

export const COVERAGE_DEPT_MEMBERS: { [siteDept: string]: number } = {
  '1-1': 30, '1-2': 12, '1-3': 4, '1-4': 6, '1-5': 6,
  '2-1': 6, '2-2': 4, '2-3': 1, '2-4': 2, '2-5': 1,
}

const role = (count: number, roleId: number, critical = false, locationNodeId: number | null = null): ICoverageRow => ({
  count, requirementType: 'role', roleId, capabilityId: null, critical, locationNodeId,
})
const cap = (count: number, capabilityId: number, critical = false): ICoverageRow => ({
  count, requirementType: 'capability', roleId: null, capabilityId, critical, locationNodeId: null,
})

let nextModelId = 100
const model = (departmentId: number, siteNodeId: number, shiftId: number, rows: ICoverageRow[], effectiveFrom = '2026-06-01'): ICoverageModel => ({
  id: nextModelId++, departmentId, siteNodeId, shiftId, rows, effectiveFrom, createdBy: 'Milos', createdAt: effectiveFrom + 'T08:00:00.000Z',
})

const models: ICoverageModel[] = [
  model(1, 1, 1, [role(10, 1), role(2, 2)]),
  model(2, 1, 1, [role(5, 3)]),
  model(3, 1, 1, [role(2, 4), cap(1, 101, true)]),
  model(4, 1, 1, [role(1, 6), role(2, 7)]),
  model(5, 1, 1, [role(1, 9, true, 11), role(1, 9, false, 12)]),
  model(1, 1, 2, [role(8, 1), role(2, 2)]),
  model(2, 1, 2, [role(4, 3)]),
  model(3, 1, 2, [cap(1, 101, true)]),
  model(4, 1, 2, [role(1, 7), role(1, 8)]),
  model(5, 1, 2, [role(1, 9, true, 11), role(1, 9, false, 12)]),
  model(1, 1, 3, [role(5, 1)]),
  model(2, 1, 3, [role(2, 3)]),
  model(5, 1, 3, [role(2, 9)]),
  model(1, 2, 1, [role(6, 1)]),
  model(5, 2, 1, [role(1, 9, true, 21)]),
  model(2, 2, 2, [role(3, 3)]),
]

interface IDemoPerson {
  userId: number
  name: string
  roleLabel: string
  rowIdx: number
  active: 'present' | 'late' | 'absent' | 'break'
  future: 'confirmed' | 'no_response' | 'unavailable'
}

const P = (userId: number, name: string, roleLabel: string, rowIdx: number, active: IDemoPerson['active'] = 'present', future: IDemoPerson['future'] = 'confirmed'): IDemoPerson => ({ userId, name, roleLabel, rowIdx, active, future })

const POOLS: { [key: string]: IDemoPerson[] } = {
  '1-1-1': [
    P(1, 'Peter M.', 'Operator', 0), P(2, 'Michael K.', 'Operator', 0), P(3, 'John S.', 'Operator', 0),
    P(4, 'Daniel R.', 'Operator', 0), P(5, 'Anna V.', 'Operator', 0), P(6, 'Luka J.', 'Operator', 0),
    P(7, 'Stefan D.', 'Operator', 0), P(8, 'Emma W.', 'Operator', 0, 'late'), P(9, 'Noah B.', 'Operator', 0),
    P(10, 'Mia T.', 'Operator', 0), P(11, 'Igor P.', 'Operator', 0),
    P(12, 'Omar H.', 'Truck driver', 1), P(13, 'Sara K.', 'Truck driver', 1),
  ],
  '1-2-1': [
    P(20, 'Grace M.', 'Plant operator', 0), P(21, 'Filip N.', 'Plant operator', 0), P(22, 'Jana R.', 'Plant operator', 0),
    P(23, 'Marko S.', 'Plant operator', 0), P(24, 'Lea D.', 'Plant operator', 0), P(25, 'Tom H.', 'Plant operator', 0, 'break'),
  ],
  '1-3-1': [
    P(30, 'Nicholas T.', 'Mechanic', 0), P(31, 'Sofia R.', 'Mechanic', 0), P(32, 'Ivan K.', 'MECH-2', 1),
  ],
  '1-4-1': [
    P(40, 'Thomas P.', 'Camp supervisor', 0), P(41, 'Robert L.', 'Cleaner', 1), P(42, 'Nina V.', 'Cleaner', 1),
  ],
  '1-5-1': [
    P(50, 'Aleks G.', 'Gate guard', 0), P(51, 'Marco B.', 'Gate guard', 1),
  ],
  '1-1-2': [
    P(60, 'Pavle M.', 'Operator', 0), P(61, 'Dario F.', 'Operator', 0), P(62, 'Tara Z.', 'Operator', 0),
    P(63, 'Vuk L.', 'Operator', 0), P(64, 'Ena S.', 'Operator', 0), P(65, 'Rade K.', 'Operator', 0),
    P(66, 'Iva B.', 'Operator', 0), P(67, 'Ognjen T.', 'Operator', 0),
    P(68, 'Milan D.', 'Truck driver', 1), P(69, 'Jovana P.', 'Truck driver', 1),
  ],
  '1-2-2': [
    P(70, 'Petra N.', 'Plant operator', 0), P(71, 'Andrej V.', 'Plant operator', 0),
    P(72, 'Katarina J.', 'Plant operator', 0), P(73, 'Boris M.', 'Plant operator', 0),
  ],
  '1-3-2': [P(74, 'Ivan K.', 'MECH-2', 0)],
  '1-4-2': [P(75, 'Robert L.', 'Cleaner', 0), P(76, 'Zora D.', 'Cook', 1)],
  '1-5-2': [
    P(77, 'Diego Soto', 'Gate guard', 0, 'absent', 'no_response'),
    P(78, 'Marco B.', 'Gate guard', 1),
  ],
  '1-1-3': [
    P(80, 'Nemanja S.', 'Operator', 0), P(81, 'Olga F.', 'Operator', 0), P(82, 'Damir H.', 'Operator', 0),
    P(83, 'Tijana R.', 'Operator', 0), P(84, 'Goran B.', 'Operator', 0),
  ],
  '1-2-3': [P(85, 'Vera N.', 'Plant operator', 0), P(86, 'Lazar K.', 'Plant operator', 0)],
  '1-5-3': [P(87, 'Ana M.', 'Gate guard', 0), P(88, 'Dusan V.', 'Gate guard', 0)],
  '2-1-1': [
    P(90, 'Nikola T.', 'Operator', 0), P(91, 'Maja S.', 'Operator', 0), P(92, 'Zoran P.', 'Operator', 0),
    P(93, 'Elena D.', 'Operator', 0), P(94, 'Vlado K.', 'Operator', 0),
  ],
  '2-5-1': [P(95, 'Branko J.', 'Gate guard', 0)],
  '2-2-2': [P(96, 'Sanja L.', 'Plant operator', 0), P(97, 'Dejan R.', 'Plant operator', 0), P(98, 'Mira B.', 'Plant operator', 0)],
}

const todayStr = () => new Date().toISOString().slice(0, 10)

const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number)
  return h * 60 + m
}

export const coveragePhaseFor = (windowFrom: string, windowTo: string, dateStr?: string): 'active' | 'future' | 'past' => {
  const date = dateStr || todayStr()
  const today = todayStr()
  if (date < today) return 'past'
  if (date > today) return 'future'
  const now = new Date()
  const nowMin = now.getHours() * 60 + now.getMinutes()
  const from = toMin(windowFrom)
  const to = toMin(windowTo)
  if (to < from) return nowMin >= from ? 'active' : 'future'
  if (nowMin >= to) return 'past'
  if (nowMin >= from) return 'active'
  return 'future'
}

export const activeModelFor = (departmentId: number, siteNodeId: number, shiftId: number, dateStr?: string): ICoverageModel | null => {
  const date = dateStr || todayStr()
  const candidates = models
    .filter((m) => m.departmentId === departmentId && m.siteNodeId === siteNodeId && m.shiftId === shiftId && m.effectiveFrom <= date)
    .sort((a, b) => (a.effectiveFrom < b.effectiveFrom ? 1 : -1))
  const winner = candidates[0] || null
  return winner && winner.rows.length ? winner : null
}

export const coverageRowLabel = (row: ICoverageRow): string => {
  const req = row.requirementType === 'role'
    ? roleDefs.find((r) => r.id === row.roleId)?.name || 'Role'
    : (capDefs.find((c) => c.id === row.capabilityId)?.name || 'Capability') + ' (capability)'
  const loc = row.locationNodeId ? locDefs.find((l) => l.id === row.locationNodeId)?.name : null
  return `${row.count}× ${req}${loc ? ` @ ${loc}` : ''}`
}

const checkedIn = (p: IDemoPerson) => p.active === 'present' || p.active === 'late' || p.active === 'break'

const computeCell = (departmentId: number, siteNodeId: number, shiftId: number, dateStr?: string): ICoverageMatrixCell | null => {
  const m = activeModelFor(departmentId, siteNodeId, shiftId, dateStr)
  if (!m) return null
  const shift = shiftDefs.find((s) => s.shiftId === shiftId)
  const phase = coveragePhaseFor(shift?.windowFrom || '06:00', shift?.windowTo || '14:00', dateStr)
  const pool = POOLS[`${siteNodeId}-${departmentId}-${shiftId}`] || []
  const counted = phase === 'future' ? pool.filter((p) => p.future === 'confirmed') : pool.filter(checkedIn)
  const required = m.rows.reduce((acc, r) => acc + r.count, 0)
  const covered = counted.length
  const rowsBreakdown: ICoverageCellRowBreakdown[] = m.rows.map((r, idx) => {
    const matched = counted.filter((p) => p.rowIdx === idx).length
    return {
      label: coverageRowLabel(r),
      required: r.count,
      covered: Math.min(matched, r.count),
      critical: r.critical,
      locationName: r.locationNodeId ? locDefs.find((l) => l.id === r.locationNodeId)?.name || null : null,
    }
  })
  const criticalGap = rowsBreakdown.some((r) => r.critical && r.covered < r.required)
  return {
    shiftId,
    phase,
    required,
    covered,
    confirmed: phase === 'future' ? covered : null,
    rostered: phase === 'future' ? pool.filter((p) => p.future !== 'unavailable').length : null,
    criticalGap,
    rowsBreakdown,
  }
}

export const buildDemoMatrix = (siteNodeId: number, dateStr?: string): ICoverageMatrixResponse => {
  const departments = deptDefs
    .map((d) => {
      const cells = shiftDefs
        .map((s) => computeCell(d.id, siteNodeId, s.shiftId, dateStr))
        .filter(Boolean) as ICoverageMatrixCell[]
      return { departmentId: d.id, name: d.name, cells }
    })
    .filter((d) => d.cells.length > 0)
  return {
    siteName: siteDefs.find((s) => s.id === siteNodeId)?.name || 'Site',
    shifts: shiftDefs.map(({ shiftId, name, windowFrom, windowTo }) => ({ shiftId, name, windowFrom, windowTo })),
    departments,
  }
}

const STATUS_LABEL: { [k: string]: string } = {
  present: 'present', late: 'late', absent: 'absent', break: 'break',
  confirmed: 'confirmed', 'no_response': 'no response', unavailable: 'unavailable',
}

export const buildDemoCell = (siteNodeId: number, departmentId: number, shiftId: number, dateStr?: string): ICoverageCellResponse | null => {
  const cell = computeCell(departmentId, siteNodeId, shiftId, dateStr)
  const m = activeModelFor(departmentId, siteNodeId, shiftId, dateStr)
  if (!cell || !m) return null
  const shift = shiftDefs.find((s) => s.shiftId === shiftId)
  const pool = POOLS[`${siteNodeId}-${departmentId}-${shiftId}`] || []
  const people = pool.map((p) => ({
    userId: p.userId,
    name: p.name,
    roleLabel: p.roleLabel,
    status: STATUS_LABEL[cell.phase === 'future' ? p.future : p.active],
    postName: m.rows[p.rowIdx]?.locationNodeId
      ? locDefs.find((l) => l.id === m.rows[p.rowIdx].locationNodeId)?.name || null
      : null,
  }))
  return {
    departmentId,
    departmentName: deptDefs.find((d) => d.id === departmentId)?.name || 'Department',
    shiftId,
    shiftName: shift?.name || 'Shift',
    windowFrom: shift?.windowFrom || '',
    windowTo: shift?.windowTo || '',
    phase: cell.phase,
    required: cell.required,
    covered: cell.covered,
    confirmed: cell.confirmed,
    rostered: cell.rostered,
    criticalGap: cell.criticalGap,
    rowsBreakdown: cell.rowsBreakdown,
    people,
  }
}

export const buildDemoWidgetSummary = (siteNodeId = 1): IShiftCoverageSummaryResponse => {
  const legacyFullDayId = registrySynced ? null : 4
  const shifts = shiftDefs
    .filter((s) => s.shiftId !== legacyFullDayId)
    .map((s) => {
      const deptCells = deptDefs
        .map((d) => ({ dept: d, cell: computeCell(d.id, siteNodeId, s.shiftId) }))
        .filter((x) => x.cell) as { dept: { id: number; name: string }; cell: ICoverageMatrixCell }[]
      const required = deptCells.reduce((a, x) => a + x.cell.required, 0)
      const covered = deptCells.reduce((a, x) => a + x.cell.covered, 0)
      const criticalGap = deptCells.some((x) => x.cell.criticalGap)
      const phase = deptCells[0]?.cell.phase || coveragePhaseFor(s.windowFrom, s.windowTo)
      return {
        shiftId: s.shiftId,
        name: s.name,
        windowFrom: s.windowFrom,
        windowTo: s.windowTo,
        phase,
        required,
        covered,
        confirmed: phase === 'future' ? covered : null,
        criticalGap,
        departments: deptCells.map((x) => {
          const gapRow = x.cell.rowsBreakdown.find((r) => r.critical && r.covered < r.required)
          return {
            departmentId: x.dept.id,
            name: x.dept.name,
            required: x.cell.required,
            covered: x.cell.covered,
            criticalGap: x.cell.criticalGap,
            gapLabel: gapRow
              ? `${gapRow.label.replace(/^\d+× /, '')}${x.cell.phase === 'future' ? ' UNCONFIRMED' : ' unfilled'}`
              : null,
          }
        }),
      }
    })
    .filter((s) => s.required > 0)

  const fullDay = registrySynced ? null : shiftDefs.find((s) => s.shiftId === 4)
  if (fullDay) {
    shifts.push({
      shiftId: 4, name: fullDay.name, windowFrom: fullDay.windowFrom, windowTo: fullDay.windowTo,
      phase: coveragePhaseFor(fullDay.windowFrom, fullDay.windowTo), required: 0, covered: siteNodeId === 1 ? 3 : 0,
      confirmed: null, criticalGap: false, departments: [],
    })
  }

  const noReqs = shiftDefs.filter(
    (s) => s.shiftId !== legacyFullDayId && !deptDefs.some((d) => activeModelFor(d.id, siteNodeId, s.shiftId)),
  )
  return {
    siteName: siteDefs.find((s) => s.id === siteNodeId)?.name || 'Site',
    shifts,
    shiftsWithoutRequirements: noReqs.map((s) => ({ shiftId: s.shiftId, name: s.name })),
  }
}

export const demoModelsFor = (siteNodeId: number, departmentId: number): ICoverageModel[] =>
  models.filter((m) => m.siteNodeId === siteNodeId && m.departmentId === departmentId)

export const saveDemoModelVersions = (payload: {
  departmentId: number
  siteNodeId: number
  effectiveFrom: string
  shifts: { shiftId: number; rows: ICoverageRow[] }[]
}): number => {
  let created = 0
  payload.shifts.forEach((entry) => {
    const current = activeModelFor(payload.departmentId, payload.siteNodeId, entry.shiftId, payload.effectiveFrom)
    const currentRows = JSON.stringify(current?.rows || [])
    if (currentRows === JSON.stringify(entry.rows)) return
    models.push(model(payload.departmentId, payload.siteNodeId, entry.shiftId, entry.rows, payload.effectiveFrom))
    created += 1
  })
  return created
}
